'use client';

import { useRef, useState } from 'react';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { SpaProductCardItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/SpaProductCardItemFragmentFragmentDoc';
import { ProductCardGridItem } from '@packages/modules/src/ProductCardV2/ProductCardV2.GridItem.client';

import { useOnFirstBecameVisible } from '../../hooks/useOnFirstBecameVisible';
import { SpaBeaconImage } from '../SpaBeaconImage/SpaBeaconImage';
import type { SpaProductCardContentProps } from './SpaProductCard.Content';
import { SpaProductCardContent } from './SpaProductCard.Content';

/* GraphQL */ `
  fragment SpaProductCardItemFragment on SponsoredStylePreviewItem {
    ...SpaProductCardContentFragment
    styleId
    tracking {
      onLoad
      onView
      adId
    }
  }
`;

export type SpaProductCardProps = Omit<
  SpaProductCardContentProps,
  'maskedItem' | 'hasReceivedFocus'
> & {
  maskedItem: FragmentType<typeof SpaProductCardItemFragmentFragmentDoc>;
  onFirstBecameVisible?: (parameters: { pageNumber: number; indexInPage?: number }) => void;
};

/**
 * ProductCard component used to render sponsored products
 * */
export const SpaProductCard = ({
  onFirstBecameVisible,
  maskedItem,
  ...contentProps
}: SpaProductCardProps) => {
  const { pageNumber } = contentProps;

  const [hasReceivedFocus, setHasReceivedFocus] = useState(false);
  const item = unmask<typeof SpaProductCardItemFragmentFragmentDoc>(maskedItem);

  const ref = useRef<HTMLLIElement>(null);
  useOnFirstBecameVisible(ref, () => {
    onFirstBecameVisible?.({ pageNumber });
  });

  return (
    <ProductCardGridItem
      className="group pb-2"
      ref={ref}
      id={item.styleId}
      onFocus={() => {
        setHasReceivedFocus(true);
      }}
    >
      <SpaProductCardContent
        hasReceivedFocus={hasReceivedFocus}
        maskedItem={item}
        adId={item.tracking.adId ?? undefined}
        {...contentProps}
      />

      {item.tracking.onLoad && <SpaBeaconImage src={item.tracking.onLoad} name="onLoad" />}
      {item.tracking.onView && <SpaBeaconImage src={item.tracking.onView} name="onView" />}
    </ProductCardGridItem>
  );
};
